import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { whatsappOrderUrl } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { getPricing } from "../lib/api";
import { findSector, SECTORS } from "../lib/sectors";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

const HERO_IMG =
  "https://images.unsplash.com/photo-1639690222869-1e608aa51f82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGNsZWFuJTIwZm9sZGVkJTIwd2hpdGUlMjBsaW5lbnxlbnwwfHx8fDE3ODMxNjEwODJ8MA&ixlib=rb-4.1.0&q=85";

export default function SectorLanding() {
  const { sectorSlug } = useParams();
  const sector = findSector(sectorSlug);
  const [pricing, setPricing] = useState([]);

  useEffect(() => {
    getPricing().then((d) => setPricing(d.services || [])).catch(() => setPricing([]));
  }, []);

  if (!sector) {
    return <Navigate to="/" replace />;
  }

  const path = `/laundry/${sector.slug}`;
  const nearbyList = (sector.nearby || []).slice(0, 3).join(", ");
  const title = `Laundry & Dry Cleaning in ${sector.name}, Gurugram`;
  const description =
    `RIZ N WASH offers doorstep laundry, ironing and dry cleaning in ${sector.name}, Gurugram. ` +
    `Free pickup & delivery within 48 hours${nearbyList ? ` — covering ${nearbyList}` : ""}. ` +
    `Wash & Iron from ₹19/piece, dry cleaning from ₹149/piece.`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: `RIZ N WASH · ${sector.name}`,
      description,
      url: `${SITE}${path}`,
      telephone: "+91-73032-41330",
      priceRange: "₹₹",
      areaServed: {
        "@type": "Place",
        name: `${sector.name}, Gurugram`,
        containedInPlace: { "@type": "City", name: "Gurugram" },
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: sector.name,
        addressRegion: "Haryana",
        addressCountry: "IN",
      },
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "08:00",
        closes: "20:00",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Service areas", item: `${SITE}/areas` },
        { "@type": "ListItem", position: 3, name: sector.name, item: `${SITE}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `Do you offer laundry pickup in ${sector.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Yes — RIZ N WASH picks up, cleans and delivers laundry across ${sector.name} and nearby areas${nearbyList ? ` (${nearbyList})` : ""}. Free pickup on orders above ₹299.`,
          },
        },
        {
          "@type": "Question",
          name: "How long does it take?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Standard turnaround is 48 hours. Wash & Iron is often returned within 24 hours if picked up before noon.",
          },
        },
        {
          "@type": "Question",
          name: "How can I pay?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "UPI (GPay, PhonePe, Paytm), cash on delivery, or your saved RIZ N WASH credits.",
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid={`sector-page-${sector.slug}`}>
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />
      <Nav />

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-8 md:pt-24 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <motion.nav
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="overline mb-4 flex flex-wrap items-center gap-2 text-rw-ink3"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-rw-ink">Home</Link>
              <span aria-hidden>·</span>
              <span>Service areas</span>
              <span aria-hidden>·</span>
              <span className="text-rw-ink2">{sector.name}</span>
            </motion.nav>
            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
              className="font-display font-light tracking-tight text-rw-ink leading-[0.98] text-4xl sm:text-5xl lg:text-[5.5rem]"
              data-testid="sector-heading"
            >
              Laundry &amp; dry cleaning<br />
              in <em className="italic font-normal text-rw-blue">{sector.name}</em>.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
              className="mt-6 md:mt-8 text-base md:text-xl text-rw-ink2 max-w-xl leading-relaxed"
            >
              Free doorstep pickup across {sector.name}
              {nearbyList ? ` and nearby ${nearbyList}` : ""}.
              We wash, iron and dry-clean — folded and back to your door in 48 hours.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
              className="mt-6 md:mt-10 flex flex-wrap gap-3 items-center"
            >
              <a
                href={whatsappOrderUrl(`Hi RIZ N WASH! I'd like a laundry pickup in ${sector.name}, Gurugram.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-dark rounded-full px-6 md:px-7 py-3 md:py-3.5 text-sm md:text-base font-semibold"
                data-testid="sector-book-btn"
              >
                Book a pickup in {sector.name} →
              </a>
              <a
                href="tel:+917303241330"
                className="rounded-full px-5 md:px-6 py-3 md:py-3.5 text-sm md:text-base font-semibold border border-rw-ink/20 text-rw-ink hover:bg-rw-ink hover:text-white transition-colors"
                data-testid="sector-call-btn"
              >
                Call +91 73032 41330
              </a>
            </motion.div>
          </div>
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.0, ease: EASE, delay: 0.2 }}
              className="relative rounded-2xl overflow-hidden border border-rw-ink/10 aspect-[4/5] md:aspect-[3/4]"
            >
              <img src={HERO_IMG} alt={`Laundry pickup in ${sector.name}`} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-rw-bg2/90 backdrop-blur rounded-xl p-4">
                <div className="overline mb-1">Serving</div>
                <div className="font-display text-lg md:text-xl">{sector.name}, Gurugram</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing snippet for local rich results */}
      {pricing.length > 0 && (
        <section className="bg-rw-bg2 border-y border-rw-ink/10">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="overline mb-3">Pricing in {sector.name}</div>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight">
              Same honest pricing, everywhere in Gurugram.
            </h2>
            <div className="mt-10 divide-y divide-rw-ink/10">
              {pricing.map((p, i) => (
                <div key={p.id} className="flex items-baseline gap-3 py-5">
                  <div className="w-8 font-display text-rw-ink3">{String(i + 1).padStart(2, "0")}</div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <h3 className="font-display text-2xl text-rw-ink">{p.name}</h3>
                      <span className="dotted-leader" />
                      <div className="font-display text-2xl text-rw-ink">₹{p.price}</div>
                    </div>
                    <div className="text-sm text-rw-ink2 mt-1">
                      {p.description} · {p.unit} · {p.turnaround}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Local trust copy — unique per sector to avoid duplicate-content */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20">
        <div className="overline mb-3">Why {sector.name} chooses RIZ N WASH</div>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Fresh clothes shouldn't cost you your weekend.
        </h2>
        <div className="prose prose-lg mt-8 text-rw-ink2 max-w-none leading-relaxed">
          {sector.intro && <p>{sector.intro}</p>}
          <p>
            Households across {sector.name} rely on RIZ N WASH for hygienic, on-time laundry.
            Our pickup runners cover {nearbyList || "the whole neighbourhood"} on a fixed daily route,
            so slots fill up predictably and clothes come back when we say they will.
          </p>
          <p>
            Every load is sorted by colour and fabric, washed in temperature-controlled machines,
            ironed by hand, and quality-checked before it leaves our studio at Signature Solera, Sector 107.
            Delicates, embroidery and designer garments go through our Premium Care line.
          </p>
          <p>
            Booking a pickup in {sector.name} takes under a minute — pick a two-hour slot,
            tell us what needs cleaning, and we'll WhatsApp you a confirmation.
            Free pickup &amp; delivery on all orders above ₹299.
          </p>
        </div>

        {(sector.complexes?.length || sector.landmarks?.length) && (
          <div className="mt-10 grid md:grid-cols-2 gap-6" data-testid={`sector-locality-${sector.slug}`}>
            {sector.complexes?.length ? (
              <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6">
                <div className="overline mb-2">We already serve</div>
                <div className="font-display text-lg text-rw-ink mb-3">
                  Complexes in {sector.name}
                </div>
                <ul className="text-sm text-rw-ink2 space-y-1.5">
                  {sector.complexes.map((c) => (
                    <li key={c}>· {c}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {sector.landmarks?.length ? (
              <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6">
                <div className="overline mb-2">Landmarks nearby</div>
                <div className="font-display text-lg text-rw-ink mb-3">
                  Around {sector.name}
                </div>
                <ul className="text-sm text-rw-ink2 space-y-1.5">
                  {sector.landmarks.map((l) => (
                    <li key={l}>· {l}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href={whatsappOrderUrl(`Hi RIZ N WASH! I'd like a laundry pickup in ${sector.name}, Gurugram.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-dark rounded-full px-6 py-3 font-semibold text-sm"
            data-testid="sector-cta-book"
          >
            Schedule pickup in {sector.name} →
          </a>
          <Link
            to="/plans"
            className="rounded-full px-6 py-3 border border-rw-ink/20 text-rw-ink hover:bg-rw-ink hover:text-white transition-colors text-sm font-semibold"
          >
            See weekly plans
          </Link>
        </div>
      </section>

      {/* Other areas — internal linking for SEO */}
      <section className="bg-rw-bg2 border-t border-rw-ink/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="overline mb-3">We also serve</div>
          <h3 className="font-display text-2xl md:text-3xl mb-6">Other neighbourhoods across Gurugram &amp; Delhi NCR</h3>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {SECTORS.filter((s) => s.slug !== sector.slug).map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/laundry/${s.slug}`}
                  className="block py-2 px-3 rounded-lg text-rw-ink2 hover:bg-rw-bg3 hover:text-rw-ink"
                  data-testid={`sector-link-${s.slug}`}
                >
                  Laundry in {s.name} →
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
}
